import React from 'react';
import styled from '@emotion/styled';
import moment from 'moment/moment';

import StarIcon from './Icons/personal/Star';
import HandIcon from './Icons/personal/Strong';
import HeartIcon from './Icons/personal/Heart';
import Star2Icon from './Icons/personal/Star2';
import QuranIcon from './Icons/personal/Quran';
import MosqueIcon from './Icons/personal/Mosque';
import LightsIcon from './Icons/personal/Lights';
import HijabIcon from './Icons/personal/Hijab';
import HaciIcon from './Icons/personal/Haci';
import FingerIcon from './Icons/personal/Finger';
import { getDonationMessage } from 'utils/donationMessage';

function formatAmount(amount) {
  if (amount === null || amount === undefined) {
    return '0,00';
  }

  if (typeof amount === 'string' && amount.includes(',')) {
    return amount;
  }

  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
}

function getDisplayAvatar(selectedAvatar, iconType) {
  if (
    selectedAvatar &&
    [
      'star',
      'heart',
      'hand',
      'star2',
      'finger',
      'haci',
      'hijab',
      'mosque',
      'quran',
      'lights',
    ].includes(selectedAvatar)
  ) {
    return selectedAvatar;
  }

  if (iconType === 'heart1') return 'heart';

  return iconType || 'star';
}

function getIconConfig(resolvedIconType) {
  switch (resolvedIconType) {
    case 'star':
      return {
        icon: <StarIcon />,
        iconColor: '#51A2FF',
        iconBg: '#EFF6FF',
      };

    case 'heart':
      return {
        icon: <HeartIcon />,
        iconColor: '#FB64B6',
        iconBg: '#FDF2F8',
      };

    case 'star2':
      return {
        icon: <Star2Icon />,
        iconColor: '#BDB4FE',
        iconBg: '#F4F3FF',
      };

    case 'finger':
      return {
        icon: <FingerIcon />,
        iconColor: '#717BBC',
        iconBg: '#F8F9FC',
      };

    case 'haci':
      return {
        icon: <HaciIcon />,
        iconColor: '#84CAFF',
        iconBg: '#EFF8FF',
      };

    case 'hijab':
      return {
        icon: <HijabIcon />,
        iconColor: '#FAA7E0',
        iconBg: '#FDF2FA',
      };

    case 'mosque':
      return {
        icon: <MosqueIcon />,
        iconColor: '#FF9C66',
        iconBg: '#FFF4ED',
      };

    case 'quran':
      return {
        icon: <QuranIcon />,
        iconColor: '#79573A',
        iconBg: '#F7F5F3',
      };

    case 'lights':
      return {
        icon: <LightsIcon />,
        iconColor: '#FAC515',
        iconBg: '#FEFBE8',
      };

    case 'hand':
      return {
        icon: <HandIcon />,
        iconColor: '#FF8904',
        iconBg: '#FFF7ED',
      };

    default:
      return {
        icon: <StarIcon />,
        iconColor: '#51A2FF',
        iconBg: '#EFF6FF',
      };
  }
}

function getFallbackName({ iconType, anonymous, name }) {
  if (!anonymous && name) return name;

  switch (iconType) {
    case 'heart':
      return 'Eine herzliche Seele';

    case 'hand':
      return 'Ein Bruder im Herzen';

    default:
      return 'Eine herzliche Seele';
  }
}

export function SupportListItem({
  iconType,
  name,
  anonymous,
  amount,
  city,
  time,
  custom_city,
  custom_message,
  message,
  selected_avatar,
  custom_name,
  project,
  ticketCount,
}) {
  const resolvedIconType = getDisplayAvatar(selected_avatar, iconType);

  const displayName = anonymous ? null : custom_name || name;
  const displayCity = anonymous ? '' : custom_city || city;

  const { icon, iconColor, iconBg } = getIconConfig(resolvedIconType);

  const personName = getFallbackName({
    iconType: resolvedIconType,
    anonymous,
    name: displayName,
  });

  const location = displayCity ? `${displayCity}` : '';

  const count = Number(ticketCount || 1);
  const ticketText = count === 1 ? 'Ein Ticket' : `${count} Tickets`;

  const amountText = project?.is_ticketable
    ? ticketText
    : `${formatAmount(amount)} €`;
  const showContribution = !project?.is_patreon;

  const commentText = getDonationMessage({ custom_message, message });

  return (
    <ItemCard>
      <InnerContainer>
        <IconWrapper $bg={iconBg} $color={iconColor}>
          {icon}
        </IconWrapper>

        <TextContent>
          <MainText>
            {personName}
            {location && location !== '' && (
              <>
                <LocationText> aus </LocationText>
                {location}
              </>
            )}
            {showContribution && (
              <ContributionText>
                {'- '}
                <AmountText>{amountText}</AmountText>
              </ContributionText>
            )}
          </MainText>
          {commentText && <CommentText>{commentText}</CommentText>}
          <TimeText>{time ? moment.unix(Number(time)).fromNow() : ''}</TimeText>
        </TextContent>
      </InnerContainer>
    </ItemCard>
  );
}

const ItemCard = styled.div`
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  width: 100%;
  min-height: 80px;

  padding: 16px;

  background: #ffffff;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  width: 100%;
  min-height: 48px;

  padding: 0;
  gap: 12px;
`;

const IconWrapper = styled.div`
  
  
  width: 48px;
  height: 48px;
  flex: 0 0 48px;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  padding: 0 14px;

  border-radius: 999999px;
  border: ${({ $active }) =>
    $active ? '2px solid #669F2A' : '1px solid #E5E7EB'};
  background: ${({ $bg }) => $bg || '#fff'};
  color: ${({ $color }) => $color || '#111'};

  svg {
    width: 20px;
    height: 20px;
  }

`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  min-width: 0;
  flex: 1;

  padding: 0;
  gap: 4px;
`;

const MainText = styled.div`
  max-width: 100%;
  
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  /* identical to box height, or 138% */
  letter-spacing: -0.150391px;

  color: #3f621a;

  //white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LocationText = styled.span`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  letter-spacing: -0.150391px;
  color: #0a0a0a;
`;

const ContributionText = styled.span`
  display: inline-block;
  margin-left: 4px;
  white-space: nowrap;
`;

const AmountText = styled.span`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  letter-spacing: -0.150391px;
  color: #0a0a0a;
`;

const CommentText = styled.div`
  /* Text sm/Regular */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;

  color: #717680;
  overflow-wrap: break-word;

  word-break: break-word;
`;

const TimeText = styled.div`
  width: 100%;
  
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  /* Text xs/Regular */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  /* identical to box height, or 150% */

  color: #717680;

  
`;
